/**
 * Standard error interfaces for Strykr.ai
 * Provides consistent typing for error handling across the application
 */

/**
 * Base error interface that all application errors should implement
 */
export interface AppError {
  message: string;
  code?: string;
  name?: string;
  status?: number;
  details?: Record<string, unknown>;
}

/**
 * API error response structure
 */
export interface ApiError extends AppError {
  status: number;
  endpoint?: string;
  timestamp?: string;
}

/**
 * Authentication error structure
 * Compatible with Supabase AuthError but more specific
 */
export interface AuthenticationError extends AppError {
  code: string;
  // Additional auth-specific fields
  credentialType?: 'email' | 'password' | 'token' | 'session';
  flowType?: 'signin' | 'signup' | 'reset' | 'verification';
}

/**
 * Validation error for form input validation
 */
export interface ValidationError extends AppError {
  field?: string;
  value?: unknown;
  constraint?: string;
}

/**
 * Type guard to check if an error is an AppError
 */
export function isAppError(error: unknown): error is AppError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as AppError).message === 'string'
  );
}

/**
 * Convert unknown error to AppError
 */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) {
    return error;
  }
  
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      details: { stack: error.stack }
    };
  }
  
  return {
    message: typeof error === 'string' ? error : 'Unknown error occurred',
    name: 'UnknownError',
    details: { originalError: error }
  };
}
